import { getCachedData, setCachedData } from './cacheUtils';

const NEWSLETTER_CONFIG_PATH = 'config/newsletter-config.json';
const NEWSLETTER_STATUS_PATH = 'content/newsletter-status.json';

const CHUNK_SIZE = 10;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
const cache = new Map();
const permanentCache = new Map();
const MAX_RETRIES = 3;
const RETRY_DELAY = 1000;

const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

export const fetchWithRetry = async (url, options, retries = 0) => {
  try {
    const response = await fetch(url, options);
    if (!response.ok) {
      if (retries < MAX_RETRIES) {
        await sleep(RETRY_DELAY * (retries + 1));
        return fetchWithRetry(url, options, retries + 1);
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return response;
  } catch (error) {
    if (retries < MAX_RETRIES) {
      await sleep(RETRY_DELAY * (retries + 1));
      return fetchWithRetry(url, options, retries + 1);
    }
    throw new Error(error.message || 'Failed to fetch');
  }
};

export const fetchNewsletterConfig = async () => {
  try {
    // Check permanent cache first
    const permanentKey = 'newsletterConfig';
    if (permanentCache.has(permanentKey)) {
      return permanentCache.get(permanentKey);
    }

    const cacheKey = 'newsletterConfig';
    const cached = cache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
      return cached.data;
    }

    const response = await fetch(
      `https://api.github.com/repos/${process.env.REACT_APP_REPO_OWNER}/${process.env.REACT_APP_REPO_NAME}/contents/${NEWSLETTER_CONFIG_PATH}`,
      {
        headers: {
          'Authorization': `Bearer ${process.env.REACT_APP_GITHUB_TOKEN}`,
          'Accept': 'application/vnd.github.v3+json',
        }
      }
    );

    if (response.ok) {
      const data = await response.json();
      const result = {
        config: JSON.parse(atob(data.content)),
        sha: data.sha
      };
      
      // Store in both caches
      permanentCache.set(permanentKey, result);
      cache.set(cacheKey, {
        data: result,
        timestamp: Date.now()
      });
      
      return result;
    }
    return {
      config: {
        scheduleTime: '09:00',
        timeZone: 'Asia/Kolkata',
        frequency: 'daily',
        lastRun: null
      },
      sha: null
    };
  } catch (error) {
    console.error('Error fetching newsletter config:', error);
    return { config: {}, sha: null };
  }
};

export const saveNewsletterConfig = async (config, sha = null) => {
  try {
    const content = btoa(JSON.stringify(config, null, 2));
    const body = {
      message: 'Update newsletter configuration',
      content,
      branch: 'main'
    };

    if (sha) body.sha = sha;

    await fetch(
      `https://api.github.com/repos/${process.env.REACT_APP_REPO_OWNER}/${process.env.REACT_APP_REPO_NAME}/contents/${NEWSLETTER_CONFIG_PATH}`,
      {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${process.env.REACT_APP_GITHUB_TOKEN}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body)
      }
    );
  } catch (error) {
    console.error('Error saving newsletter config:', error);
    throw error;
  }
};

export const fetchNewsletterStatus = async (page = 1) => {
  try {
    const permanentKey = `newsletterStatus_${page}`;
    if (permanentCache.has(permanentKey)) {
      return permanentCache.get(permanentKey);
    }

    const cacheKey = `newsletterStatus_${page}`;
    const cached = cache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
      return cached.data;
    }

    const response = await fetchWithRetry(
      `https://api.github.com/repos/${process.env.REACT_APP_REPO_OWNER}/${process.env.REACT_APP_REPO_NAME}/contents/${NEWSLETTER_STATUS_PATH}`,
      {
        headers: {
          'Authorization': `Bearer ${process.env.REACT_APP_GITHUB_TOKEN}`,
          'Accept': 'application/vnd.github.v3+json',
        }
      }
    );

    if (response.ok) {
      const data = await response.json();
      const allStatus = JSON.parse(atob(data.content));
      
      // Implement pagination
      const start = (page - 1) * CHUNK_SIZE;
      const end = start + CHUNK_SIZE;
      const paginatedStatus = allStatus.slice(start, end);
      
      const result = {
        status: paginatedStatus,
        sha: data.sha,
        totalPages: Math.ceil(allStatus.length / CHUNK_SIZE),
        currentPage: page
      };

      // Store in both caches
      permanentCache.set(permanentKey, result);
      cache.set(cacheKey, {
        data: result,
        timestamp: Date.now()
      });

      // Preload next page in background
      if (result.currentPage < result.totalPages) {
        prefetchNextPage(result.currentPage);
      }

      return result;
    }
    return { status: [], sha: null, totalPages: 0, currentPage: 1 };
  } catch (error) {
    console.error('Error fetching newsletter status:', error);
    return { status: [], sha: null, totalPages: 0, currentPage: 1 };
  }
};

// Add batch loading utility
export const batchFetchNewsletterData = async () => {
  try {
    const [configData, statusData] = await Promise.all([
      fetchNewsletterConfig(),
      fetchNewsletterStatus(1)
    ]);

    return {
      config: configData.config,
      status: statusData.status,
      configSha: configData.sha,
      statusSha: statusData.sha,
      totalPages: statusData.totalPages,
      currentPage: statusData.currentPage
    };
  } catch (error) {
    console.error('Error batch fetching newsletter data:', error);
    throw error;
  }
};

// Enhance prefetch to use permanent cache
export const prefetchNextPage = async (currentPage) => {
  try {
    const nextPage = currentPage + 1;
    const permanentKey = `newsletterStatus_${nextPage}`;
    
    if (!permanentCache.has(permanentKey)) {
      fetchNewsletterStatus(nextPage).catch(() => {}); // Silent fail for prefetch
    }
  } catch (error) {
    console.error('Error prefetching next page:', error);
  }
};

export const updateNewsletterStatus = async (status, sha = null) => {
  try {
    // Clear both caches
    permanentCache.clear();
    cache.clear();

    const content = btoa(JSON.stringify(status, null, 2));
    const body = {
      message: 'Update newsletter status',
      content,
      branch: 'main'
    };

    if (sha) {
      body.sha = sha;
    }

    await fetchWithRetry(
      `https://api.github.com/repos/${process.env.REACT_APP_REPO_OWNER}/${process.env.REACT_APP_REPO_NAME}/contents/${NEWSLETTER_STATUS_PATH}`,
      {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${process.env.REACT_APP_GITHUB_TOKEN}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body)
      }
    );
  } catch (error) {
    console.error('Error updating newsletter status:', error);
    throw error;
  }
};